import React from "react";
import { Box, Typography, Autocomplete, TextField } from "@mui/material";
import { colors, fonts } from "../../../../constants";
import FormFieldLabel from "../FormFieldLabel";
import {
  getSelectStyle,
  getHelperTextStyles
} from "../../../../utils/formUtils";
import { formatPostShortName } from "../../../../services/postSelection";

// GetProjectAssignee (the external SSO API this dropdown's options come
// from) returns PostShortName in PascalCase, unlike the lowercase
// name/email fields on the same record.
const getPostLabel = (option) => {
  const raw = option?.PostShortName || option?.postShortName || "";
  return raw ? formatPostShortName(raw) : "";
};

const getAssigneeName = (option) => option?.full_name || option?.name || "";

/**
 * AssignToField Component
 * Assign to user selection field
 */
const AssignToField = ({
  formData,
  errors,
  focusedFields,
  users,
  loadingUsers,
  handleChange,
  handleFocus,
  handleBlur
}) => {
  const helperTextBaseStyles = getHelperTextStyles();
  // alert("0000", users);
  const getSelectStyleWithFocus = (field, hasError) => {
    const isFocused = focusedFields[field];
    return getSelectStyle(field, hasError, isFocused);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <FormFieldLabel label="Assign To here" required={true} />
      <Autocomplete
        options={users}
        getOptionLabel={(option) => {
          const name = getAssigneeName(option);
          const postLabel = getPostLabel(option);
          return postLabel ? `${name} (${postLabel})` : name;
        }}
        isOptionEqualToValue={(option, value) => option.email === value.email}
        value={users.find((u) => u.email === formData.assigneeId) || null}
        onChange={(event, newValue) => {
          handleChange("assigneeId")({
            target: { value: newValue?.email || "" }
          });
        }}
        disabled={loadingUsers}
        loading={loadingUsers}
        openOnFocus
        renderOption={(props, option) => {
          const postLabel = getPostLabel(option);
          return (
            <Box
              component="li"
              {...props}
              sx={{
                display: "flex !important",
                flexDirection: "column",
                alignItems: "flex-start !important",
                gap: "2px"
              }}
            >
              <Typography sx={{ fontSize: fonts.fontSizeMd }}>
                {getAssigneeName(option)}
              </Typography>
              {postLabel && (
                <Typography
                  sx={{ fontSize: fonts.fontSizeXs, color: colors.textSecondary }}
                >
                  {postLabel}
                </Typography>
              )}
            </Box>
          );
        }}
        filterOptions={(options, params) => {
          const searchTerm = params.inputValue.toLowerCase();
          const filtered = options.filter(
            (option) =>
              getAssigneeName(option).toLowerCase().includes(searchTerm) ||
              getPostLabel(option).toLowerCase().includes(searchTerm)
          );
          return filtered;
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            onFocus={handleFocus("assigneeId")}
            onBlur={handleBlur("assigneeId")}
            inputProps={{
              ...params.inputProps,
              readOnly: false,
              autoComplete: "off"
            }}
            placeholder={
              loadingUsers ? "Loading users..." : "Type to search user..."
            }
            error={!!errors.assigneeId}
            sx={{
              "& .MuiOutlinedInput-root": {
                ...getSelectStyleWithFocus("assigneeId", !!errors.assigneeId),
                padding: "4px 9px",
                "& fieldset": {
                  border: "none"
                },
                "&:hover fieldset": {
                  border: "none"
                },
                "&.Mui-focused fieldset": {
                  border: "none"
                },
                "&.Mui-disabled": {
                  opacity: 0.6,
                  cursor: "not-allowed"
                }
              },
              "& .MuiInputBase-input": {
                padding: "8px 4px",
                fontSize: fonts.fontSizeMd,
                cursor: "text"
              }
            }}
          />
        )}
        sx={{
          width: "100%",
          "& .MuiAutocomplete-inputRoot": {
            padding: "0 !important"
          },
          "& .MuiSvgIcon-root": {
            color: "#1b2538"
          },
          "& .MuiAutocomplete-tag": {
            backgroundColor: colors.primaryTint,
            color: colors.primaryDark
          },
          "& .MuiAutocomplete-option": {
            "&.Mui-focused": {
              backgroundColor: colors.primaryTint
            },
            '&[aria-selected="true"]': {
              backgroundColor: colors.primaryTintHover,
              "&.Mui-focused": {
                backgroundColor: colors.primaryTintHover
              }
            }
          }
        }}
        noOptionsText={!loadingUsers ? "No users available" : ""}
      />
      {!loadingUsers && errors.assigneeId && (
        <Typography
          variant="caption"
          sx={{
            ...helperTextBaseStyles,
            color: colors.error
          }}
        >
          {errors.assigneeId}
        </Typography>
      )}
    </Box>
  );
};

export default AssignToField;
